import { getDimensions } from '@apps/card/utils';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer';
import type { PageJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { EditablePage } from '@apps/card/routes/CardCustomizer/components/EditablePage';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { PageScaler } from '@apps/card/routes/CardCustomizer/components/PageScaler';
import { Adornments } from '@apps/card/routes/CardCustomizer/steps/CardDesign/Adornments';
import { PPI } from '@apps/card/routes/CardCustomizer/steps/CardDesign/config';
import { Hover } from '@apps/card/routes/CardCustomizer/steps/CardDesign/Hover';
import { Surface } from '@apps/card/routes/CardCustomizer/steps/CardDesign/Surface';
import type { ScopedUpdateHandler } from '@apps/card/routes/CardCustomizer/useCardCustomization';
import type { MonogramJson } from '@shared/websiteTemplates/websiteTemplates.types';
import { Box } from '@withjoy/joykit';
import React from 'react';
import { applyColorsToMonogram } from '@shared/utils/monogram/monogram.utils';

type BaseMonogramViewProps = Readonly<{
  json: ThemeJson | MonogramJson;
  background?: string;
  fontColor?: string;
}>;

type MonogramViewProps<T> = T extends true
  ? Omit<BaseMonogramViewProps, 'json'> & {
      json: ThemeJson;
      editable: true;
      update: ScopedUpdateHandler<PageJson>;
    }
  : BaseMonogramViewProps & { editable?: T };

const SIZE = 6 * PPI;

export const MonogramView = <T extends boolean = false>(props: MonogramViewProps<T>) => {
  const { editable, json, background, fontColor } = props;
  const front = json.card.front as PageJson;
  // Pure renderer — callers pass already-personalized content.
  const monogram = applyColorsToMonogram(front, editable ? background : 'transparent', fontColor);
  // MonogramJson.card lacks layout/envelope but getDimensions reads front.dimensions first,
  // which all monogram data provides, so the missing fields are never reached.
  const dimensions = getDimensions(json.card as ThemeJson['card']);

  if (editable) {
    return (
      <Surface padding={0}>
        <Box
          width="100%"
          maxWidth={SIZE}
          aspectRatio="1/1"
          position="relative"
          border="1px solid"
          borderColor="mono3"
          overflow="hidden"
          borderRadius={2}
          fontSize="initial"
        >
          <PageScaler>
            <EditablePage
              imageMask={fontColor ? { color: fontColor } : undefined}
              width={dimensions.width}
              height={dimensions.height}
              page={monogram}
              update={props.update}
            />
          </PageScaler>

          <Box
            position="absolute"
            inset={0}
            pointerEvents="none"
          >
            <Hover layers={monogram.layers} />
            <Adornments
              card={monogram}
              update={props.update}
            />
          </Box>
        </Box>
      </Surface>
    );
  }

  return (
    <Box
      width="100%"
      maxWidth={SIZE}
      aspectRatio="1/1"
    >
      <PageScaler>
        <Page
          imageMask={fontColor ? { color: fontColor } : undefined}
          width={dimensions.width}
          height={dimensions.height}
          page={monogram}
        />
      </PageScaler>
    </Box>
  );
};
